import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { castRef, DistributiveOmit, genericMemo, isArray } from '@direct-frontend/stdlib';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { cn } from '../../../lib/css/className/className';
import { Select, SelectWithItemsProps } from '../../Select';
import { FilterItem, FilterItemProps } from '../FilterItem';
import { FilterChip } from '../FilterChip';
import { TriggerProps } from '../../Select/Trigger/types';
import { useFilterChipAddonSizes } from '../BaseFilterChip/BaseFilterChip';
import { IconArrowDown, IconArrowUp } from '../../../icons';
import { getItemTitle, isMultiSelectPropsWithoutDataProvider } from '../../Select/utils';

getPlatformStyles({
    common: import('./FilterSelectList.css'),
});

const cls = cn('FilterSelectList');

export type FilterSelectListProps<ValueType extends string, SectionType extends string = ValueType> = DistributiveOmit<
    SelectWithItemsProps<ValueType, SectionType>,
    'size' | 'color'
> &
    Pick<FilterItemProps, 'size' | 'color' | 'onRemove' | 'isRemovable'> & {
        /** Обязатлеьный фильтр, не может быть удален */
        isRequired?: boolean;
        /** Ошибки */
        errors?: string[];
        /** Элемент, который будет отображаться слева от названия фильтра */
        addonLeft?: React.ReactNode;
    };

/**
 * Компонент для отображения фильтра с мультивыбором.
 * Выбранные опции выстраиваются справа от названия фильтра в виде чипс.
 */
export const FilterSelectList = genericMemo(function FilterSelectList<
    ValueType extends string,
    SectionType extends string = ValueType,
>(props: FilterSelectListProps<ValueType, SectionType>) {
    const {
        color,
        isRequired,
        size,
        className,
        'data-testid': dataTestId = 'FilterSelectList',
        onRemove,
        isRemovable,
        errors,
        addonLeft,
        ...restProps
    } = props;

    const isMultiSelect = isMultiSelectPropsWithoutDataProvider(restProps);

    useEffect(() => {
        // Если есть onRemove, то компонент должен открываться сразу с попапом,
        // если ничего не было выбрано, то вызывать onRemove
        if (!restProps.isOpen && (!restProps.value || (isMultiSelect && restProps.value.length === 0)) && !isRequired) {
            onRemove?.();
        }
    }, [restProps.value, restProps.isOpen, isMultiSelect, onRemove, isRequired]);

    const renderTrigger = useMemo(
        () =>
            forwardRef<HTMLElement, TriggerProps<ValueType>>(function RenderTrigger(triggerProps, forwardedRef) {
                const { isOpened, value, placeholder } = triggerProps;
                const { iconSize } = useFilterChipAddonSizes(size);
                const canRemoveItem = !restProps.isDisabled && !(isRequired && isArray(value) && value.length <= 1);

                return (
                    <FilterItem
                        isDisabled={restProps.isDisabled}
                        isRemovable={isRemovable}
                        color={color}
                        size={size}
                        data-testid={dataTestId}
                        keyChip={
                            <FilterChip
                                {...triggerProps}
                                ref={castRef(forwardedRef, 'div')}
                                data-testid={`${dataTestId}.Select`}
                                error={errors?.join('. ')}
                                addonLeft={addonLeft}
                                addonRight={
                                    isOpened ? (
                                        <IconArrowUp color={'gray'} size={iconSize} />
                                    ) : (
                                        <IconArrowDown color={'gray'} size={iconSize} />
                                    )
                                }
                            >
                                {placeholder}
                            </FilterChip>
                        }
                        onRemove={onRemove}
                    >
                        {value.map(valueItem => {
                            const key = getItemTitle(valueItem);
                            const title = getItemTitle(valueItem, true);

                            return isMultiSelect && (value.length !== 1 || !onRemove) ? (
                                <FilterValueChip
                                    key={key}
                                    onChange={canRemoveItem ? restProps.onChange : undefined}
                                    value={valueItem.value}
                                    values={value.map(item => item.value)}
                                    text={title}
                                />
                            ) : (
                                <FilterChip key={key}>{title}</FilterChip>
                            );
                        })}
                    </FilterItem>
                );
            }),
        [
            size,
            restProps.isDisabled,
            restProps.onChange,
            isRequired,
            isRemovable,
            color,
            dataTestId,
            errors,
            addonLeft,
            onRemove,
            isMultiSelect,
        ],
    );

    return (
        <Select
            size={size}
            color={color}
            className={cls('', {}, [className])}
            renderTrigger={renderTrigger}
            data-testid={`${dataTestId}.Select`}
            {...restProps}
        />
    );
});

interface FilterValueChipProps<ValueType extends string> extends Loggable {
    values: ValueType[];
    value: ValueType;
    onChange?: (value: ValueType[]) => void;
    text: React.ReactNode;
}

export function FilterValueChip<ValueType extends string>({
    values,
    value,
    text,
    onChange,
    logNode,
}: FilterValueChipProps<ValueType>) {
    const { LogProvider, logClick } = useLogger('FilterSelectListFilterValueChip', logNode);

    const onRemove = useCallback(() => {
        onChange?.(values.filter(item => item !== value));
        logClick({ type: 'removeFilterOption' });
    }, [onChange, values, value, logClick]);

    return (
        <LogProvider>
            <FilterChip
                data-testid={`FilterSelectListFilterValueChip.${value}`}
                onRemove={onChange ? onRemove : undefined}
            >
                {text}
            </FilterChip>
        </LogProvider>
    );
}
